import React, { useState, useEffect, useRef } from "react";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import RichTextEditor from "../../../components/RichTextEditor";
import EditableTitle from "../../../components/EditableTitle";
import EditableDescription from "../../../components/EditableDescription";
import styled, { keyframes } from "styled-components";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled as muiStyled } from '@mui/material/styles';
import Button from '@mui/material/Button';

export default function AddAnnouncement({ onClose, editingAnnouncement, onSave, onDelete, isSaving: propIsSaving, justSaved: propJustSaved, updateContent }) {
  const [imageUrl, setImageUrl] = useState(editingAnnouncement?.image_url || "");
  const [editorState, setEditorState] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const [initialEditorState, setInitialEditorState] = useState(null);
  const [isDraft, setIsDraft] = useState(editingAnnouncement?.content?.isDraft || false);
  const [anchorEl, setAnchorEl] = useState(null);
  const dropdownRef = useRef(null);
  const previousIdRef = useRef(editingAnnouncement?.id);

  // Update local state when editingAnnouncement changes
  useEffect(() => {
    if (editingAnnouncement) {
      setImageUrl(editingAnnouncement.image_url || "");
      setIsDraft(editingAnnouncement.content?.isDraft || false);
      
      // Only update editor state if we're switching to a different announcement
      // or if we're starting to edit (tempContent is empty)
      if (previousIdRef.current !== editingAnnouncement.id || !Object.keys(editingAnnouncement?.tempContent || {}).length) {
        const rawContent = editingAnnouncement?.tempContent?.content?.raw || editingAnnouncement.content?.raw;
        if (rawContent) {
          try {
            const contentState = convertFromRaw(rawContent);
            const newEditorState = EditorState.createWithContent(contentState);
            setInitialEditorState(newEditorState);
            setEditorState(newEditorState);
          } catch (error) {
            console.error('Error converting raw content to EditorState:', error);
            setInitialEditorState(null);
            setEditorState(null);
          }
        } else {
          setInitialEditorState(null);
          setEditorState(null);
        }
        previousIdRef.current = editingAnnouncement.id;
      }
    }
  }, [editingAnnouncement]);

  const handleEditorChange = (newState) => {
    setEditorState(newState);
    // Update tempContent with the latest editor content
    const contentData = {
      raw: convertToRaw(newState.getCurrentContent()),
      text: newState.getCurrentContent().getPlainText() || '',
      html: convertContentToHTML(newState.getCurrentContent()),
      isDraft: isDraft
    };
    updateContent('content', contentData);
  };

  const createAnnouncementData = (asDraft = false) => {
    // Get the current title and description from tempContent
    const title = editingAnnouncement?.tempContent?.title || editingAnnouncement?.title || '';
    const description = editingAnnouncement?.tempContent?.description || editingAnnouncement?.description || '';
    
    if (!title?.trim() && !asDraft) {
      alert("Please enter a title");
      return null;
    }

    const contentData = editorState ? {
      raw: convertToRaw(editorState.getCurrentContent()),
      text: editorState.getCurrentContent().getPlainText() || '',
      html: convertContentToHTML(editorState.getCurrentContent()),
    } : editingAnnouncement?.content || {};

    return {
      id: editingAnnouncement?.id,
      title: title?.trim(),
      description: description?.trim(),
      image_url: imageUrl,
      content: {
        ...contentData,
        isDraft: asDraft
      },
      type: editingAnnouncement?.type || "update",
      organization_id: editingAnnouncement?.organization_id
    };
  };

  const handleSaveDraft = async () => {
    const announcementData = createAnnouncementData(true);
    if (!announcementData) return;

    if (editingAnnouncement?.id && editingAnnouncement.id !== 'new') {
      announcementData.id = editingAnnouncement.id;
    }

    // Update the isDraft state immediately
    setIsDraft(true);
    
    await onSave(announcementData);
    handleDropdownClose();
  };

  const handlePublish = async () => {
    const announcementData = createAnnouncementData(false);
    if (!announcementData) return;

    if (editingAnnouncement?.id && editingAnnouncement.id !== 'new') {
      announcementData.id = editingAnnouncement.id;
    }

    // Update the isDraft state immediately
    setIsDraft(false);
    
    await onSave(announcementData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      onDelete(editingAnnouncement);
    }
    handleDropdownClose();
  };

  const handleDropdownClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleDropdownItemClick = (action) => {
    if (action === 'draft') {
      handleSaveDraft();
    } else if (action === 'delete') {
      handleDelete();
    } else if (action === 'publish') {
      handlePublish();
    }
    handleDropdownClose();
  };

  // Function to convert Draft.js ContentState to HTML
  const convertContentToHTML = (contentState) => {
    let html = '';
    const blocks = contentState.getBlocksAsArray();
    
    blocks.forEach(block => {
      const text = block.getText();
      if (!text) {
        html += '<br/>';
        return;
      }
      
      let blockHTML = '';
      const type = block.getType();
      const blockStart = getBlockStart(type);
      const blockEnd = getBlockEnd(type);
      
      let currentInlineStyles = [];
      let currentEntityKey = null;
      let currentEntityData = null;
      
      for (let i = 0; i < text.length; i++) {
        const inlineStyles = block.getInlineStyleAt(i).toArray();
        const entityKey = block.getEntityAt(i);
        
        // Handle style changes
        const stylesToAdd = inlineStyles.filter(style => !currentInlineStyles.includes(style));
        const stylesToRemove = currentInlineStyles.filter(style => !inlineStyles.includes(style));
        
        // Close tags for removed styles
        stylesToRemove.forEach(style => {
          blockHTML += getInlineStyleEnd(style);
        });
        
        // Open tags for new styles
        stylesToAdd.forEach(style => {
          blockHTML += getInlineStyleStart(style);
        });
        
        // Handle entity changes
        if (entityKey !== currentEntityKey) {
          // Close previous entity if exists
          if (currentEntityKey) {
            blockHTML += getEntityEnd(contentState.getEntity(currentEntityKey).getType());
          }
          
          // Open new entity if exists
          if (entityKey) {
            const entity = contentState.getEntity(entityKey);
            currentEntityData = entity.getData();
            blockHTML += getEntityStart(entity.getType(), currentEntityData);
          }
          
          currentEntityKey = entityKey;
        }
        
        // Add the character
        blockHTML += text[i];
        
        // Update current styles
        currentInlineStyles = inlineStyles;
      }
      
      // Close any remaining inline styles
      currentInlineStyles.forEach(style => {
        blockHTML += getInlineStyleEnd(style);
      });
      
      // Close any remaining entity
      if (currentEntityKey) {
        blockHTML += getEntityEnd(contentState.getEntity(currentEntityKey).getType());
      }
      
      html += blockStart + blockHTML + blockEnd;
    });
    
    return html;
  };
  
  const getBlockStart = (type) => {
    switch (type) {
      case 'header-one': return '<h1>';
      case 'header-two': return '<h2>';
      case 'header-three': return '<h3>';
      case 'unordered-list-item': return '<ul><li>';
      case 'ordered-list-item': return '<ol><li>';
      case 'blockquote': return '<blockquote>';
      default: return '<p>';
    }
  };
  
  const getBlockEnd = (type) => {
    switch (type) {
      case 'header-one': return '</h1>';
      case 'header-two': return '</h2>';
      case 'header-three': return '</h3>';
      case 'unordered-list-item': return '</li></ul>';
      case 'ordered-list-item': return '</li></ol>';
      case 'blockquote': return '</blockquote>';
      default: return '</p>';
    }
  };
  
  const getInlineStyleStart = (style) => {
    switch (style) {
      case 'BOLD': return '<strong>';
      case 'ITALIC': return '<em>';
      case 'UNDERLINE': return '<u>';
      case 'CODE': return '<code>';
      default: return '';
    }
  };
  
  const getInlineStyleEnd = (style) => {
    switch (style) {
      case 'BOLD': return '</strong>';
      case 'ITALIC': return '</em>';
      case 'UNDERLINE': return '</u>';
      case 'CODE': return '</code>';
      default: return '';
    }
  };
  
  const getEntityStart = (type, data) => {
    switch (type) {
      case 'LINK': return `<a href="${data.url}" target="_blank">`;
      default: return '';
    }
  };
  
  const getEntityEnd = (type) => {
    switch (type) {
      case 'LINK': return '</a>';
      default: return '';
    }
  };

  return (
    <AnnouncementEditor>
      {editingAnnouncement?.id === 'new' && (
        <>
          <EditableTitle
            value={editingAnnouncement?.tempContent?.title || ''}
            onChange={(e) => updateContent('title', e.target.value)}
            placeholder="Title"
          />
          <EditableDescription
            value={editingAnnouncement?.tempContent?.description || ''}
            onChange={(e) => updateContent('description', e.target.value)}
            placeholder="Description"
          />
        </>
      )}
      <ToggleMetadata onClick={() => setShowMetadata(!showMetadata)}>
        {showMetadata ? 'Hide details' : 'Show details'}
      </ToggleMetadata>
      <MetadataSection $isVisible={showMetadata}>
        <Input
          placeholder="Image Link"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
        />
        <DraftToggle>
          <input 
            type="checkbox" 
            id="isDraft" 
            checked={isDraft} 
            onChange={(e) => setIsDraft(e.target.checked)} 
          />
          <label htmlFor="isDraft">Save as draft (only visible to admins)</label>
        </DraftToggle>
      </MetadataSection>
      <RichTextEditor
        placeholder="Write your announcement..."
        onChange={handleEditorChange}
        initialState={initialEditorState}
      />
      <ButtonGroup>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <ActionButtonsContainer>
          <ButtonWrapper>
            <MainButton 
              onClick={isDraft ? handleSaveDraft : handlePublish} 
              disabled={propIsSaving}
            >
              {propIsSaving ? 'Saving...' : propJustSaved ? 'Saved!' : isDraft ? 'Update Draft' : editingAnnouncement?.id !== 'new' ? 'Save Changes' : 'Publish'}
            </MainButton>
            <DropdownButton
              onClick={handleDropdownClick}
              disabled={propIsSaving}
            >
              <ArrowDropDownIcon 
                style={{ 
                  transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)', 
                  transition: 'transform 0.2s',
                  display: 'block'
                }} 
              />
            </DropdownButton>
            <StyledMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleDropdownClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {isDraft ? (
                <>
                  {editingAnnouncement?.id !== 'new' && (
                    <StyledMenuItem $isDestructive onClick={() => handleDropdownItemClick('delete')}>
                      Delete Announcement
                    </StyledMenuItem>
                  )}
                  <StyledMenuItem onClick={() => handleDropdownItemClick('publish')}>
                    Publish
                  </StyledMenuItem>
                </>
              ) : (
                <>
                  <StyledMenuItem onClick={() => handleDropdownItemClick('draft')}>
                    Save as Draft
                  </StyledMenuItem>
                  {editingAnnouncement?.id !== 'new' && (
                    <StyledMenuItem $isDestructive onClick={() => handleDropdownItemClick('delete')}>
                      Delete Announcement
                    </StyledMenuItem>
                  )}
                </>
              )}
            </StyledMenu>
          </ButtonWrapper>
        </ActionButtonsContainer>
      </ButtonGroup>
    </AnnouncementEditor>
  );
}

const AnnouncementEditor = styled.div.attrs({
  className: 'AnnouncementEditor'
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: white;
  border-radius: 5px;
  height: 100%;
  animation: slideUp 0.3s ease-out forwards;
  .EditorContainer {
    max-height: calc(100vh - 390px); // Leave space for title, metadata, and buttons
  }
`;

const Input = styled.input.attrs({
  className: 'Input'
})`
  padding: 8px;
  border: 1px solid #e5e5e5;
  border-radius: 3px;
  font-size: 13px;
  font-family: "PT Sans";
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const ButtonGroup = styled.div.attrs({
  className: 'ButtonGroup'
})`
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  width: 100%;
  gap: 8px;
  justify-content: space-between;
`;

const ActionButtonsContainer = styled.div.attrs({
  className: 'ActionButtonsContainer'
})`
  display: flex;
  gap: 8px;
`;

const ButtonWrapper = styled.div.attrs({
  className: 'ButtonWrapper'
})`
  display: flex;
  align-items: center;
`;

const MainButton = muiStyled(Button)`
  padding: 5px 10px;
  text-transforma: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  background-color: #a855f7;
  color: white;
  border: none;
  border-radius: 3px 0px 0px 3px;
  flex: 1;
  &:hover {
    background-color: #8b45e6;
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

const DropdownButton = muiStyled(Button)`
  padding: 3.5px;
  box-sizing: border-box;
  text-transform: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  background-color: #a855f7;
  color: white;
  border: none;
  min-width: 40px;
  width: 40px;
  height: 31px;
  box-sizing: border-box;
  justify-content: center;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  overflow: hidden;
  span { 
    margin-left: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &:hover {
    background-color: #8b45e6;
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 11px;
  font-weight: 900;
  padding: 5px 10px;
  cursor: pointer;
  text-decoration: underline;
  font-family: "PT Sans";

  &:hover {
    color: black;
  }
`;

const ToggleMetadata = styled.button.attrs({
  className: 'ToggleMetadata'
})`
  background: none;
  border: none;
  color: #666;
  font-size: 11px;
  font-weight: 900;
  padding: 5px 0;
  cursor: pointer;
  text-decoration: underline;
  align-self: flex-start;
  margin: -2px 0 0px 8px;
  font-family: "PT Sans";

  &:hover {
    color: black;
  }
`;

const MetadataSection = styled.div.attrs({
  className: 'MetadataSection'
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: ${props => props.$isVisible ? 'auto' : '0'};
  opacity: ${props => props.$isVisible ? '1' : '0'};
  transition: all 0.3s ease-in-out;
  margin-bottom: ${props => props.$isVisible ? '5px' : '0'};
`;

const StyledMenu = muiStyled(Menu)`
  .MuiPaper-root {
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    position: fixed;
    margin-top: -130px !important;
  }
  
  &.MuiMenu-root {
    .MuiPaper-root {
      transform-origin: bottom center !important;
    }
  }
`;

const StyledMenuItem = muiStyled(MenuItem, {
  shouldForwardProp: prop => !prop.startsWith('$')
})`
  padding: 8px 15px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  display: flex;
  justify-content: flex-end;
  color: ${props => props.$isDestructive ? '#F44336' : 'inherit'};
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  .menu-item-content {
    display: flex;
    align-items: center;
  }
`;

const DraftToggle = styled.div.attrs({
  className: 'DraftToggle'
})`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  
  input {
    margin: 0;
  }
  
  label {
    font-size: 12px;
    color: #666;
    font-family: "PT Sans";
  }
`;